/**
 * Trunnion AI - Toast Notification Component
 */

import { useEffect } from 'react';
import { X, CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';
import { useNotificationStore, type Notification } from '@/stores/notificationStore';

const iconMap = {
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle,
  info: Info,
};

const colorMap = {
  success: 'border-green-500/30 bg-green-500/10',
  warning: 'border-yellow-500/30 bg-yellow-500/10',
  error: 'border-red-500/30 bg-red-500/10',
  info: 'border-blue-500/30 bg-blue-500/10',
};

const iconColorMap = {
  success: 'text-green-400',
  warning: 'text-yellow-400',
  error: 'text-red-400',
  info: 'text-blue-400',
};

function ToastItem({ notification }: { notification: Notification }) {
  const { removeNotification } = useNotificationStore();
  const Icon = iconMap[notification.type];

  return (
    <div
      className={`flex items-start gap-3 rounded-lg border p-4 shadow-lg backdrop-blur-sm animate-slide-in ${colorMap[notification.type]}`}
      role="alert"
    >
      <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${iconColorMap[notification.type]}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white">{notification.title}</p>
        <p className="mt-0.5 text-sm text-trunnion-slate-300">{notification.message}</p>
      </div>
      <button
        onClick={() => removeNotification(notification.id)}
        className="shrink-0 rounded p-0.5 text-trunnion-slate-400 hover:text-white"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function Toaster() {
  const { notifications } = useNotificationStore();

  // Force re-render on notification changes
  useEffect(() => {}, [notifications]);

  if (notifications.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-100 flex flex-col gap-2 w-96 max-w-[calc(100vw-2rem)]" aria-live="polite" aria-atomic="false">
      {notifications.slice(-5).map((notification) => (
        <ToastItem key={notification.id} notification={notification} />
      ))}
    </div>
  );
}
